import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
}

export interface UserRole {
  id: string;
  role: 'super_admin' | 'admin' | 'employee';
  tenant_id: string | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  domain: string | null;
  logo_url: string | null;
  is_active: boolean;
  created_at: string;
  cnpj: string | null;
  phone: string | null;
  email: string | null;
  address_street: string | null;
  address_number: string | null;
  address_complement: string | null;
  address_neighborhood: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zipcode: string | null;
  subscription_plan_id: string | null;
}

export function useUserProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as UserProfile | null;
    },
    enabled: !!user,
  });

  const { data: userRoles, isLoading: loadingRoles } = useQuery({
    queryKey: ['user_roles', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      return data as UserRole[];
    },
    enabled: !!user,
  });

  const { data: tenant, isLoading: loadingTenant } = useQuery({
    queryKey: ['tenant', userRoles?.[0]?.tenant_id],
    queryFn: async () => {
      const tenantId = userRoles?.[0]?.tenant_id;
      if (!tenantId) return null;
      
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .maybeSingle();
      
      if (error) throw error;
      return data as TenantInfo | null;
    },
    enabled: !!userRoles?.[0]?.tenant_id,
  });

  const { data: currentPlan, isLoading: loadingPlan } = useQuery({
    queryKey: ['current_subscription_plan', tenant?.subscription_plan_id],
    queryFn: async () => {
      if (!tenant?.subscription_plan_id) return null;
      
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', tenant.subscription_plan_id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenant?.subscription_plan_id,
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      if (!user) throw new Error('Usuário não autenticado');
      
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Perfil atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar perfil: ' + error.message);
    },
  });

  const uploadAvatar = async (file: File, userId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const uploadCompanyLogo = async (file: File, tenantId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${tenantId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('company-logos')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('company-logos')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const updateTenant = useMutation({
    mutationFn: async (updates: Partial<TenantInfo>) => {
      const tenantId = userRoles?.[0]?.tenant_id;
      if (!tenantId) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenantId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      toast.success('Informações da empresa atualizadas!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar empresa: ' + error.message);
    },
  });

  return {
    profile,
    userRoles,
    tenant,
    currentPlan,
    isLoading: loadingProfile || loadingRoles || loadingTenant || loadingPlan,
    updateProfile,
    updateTenant,
    uploadAvatar,
    uploadCompanyLogo,
    primaryRole: userRoles?.[0]?.role,
    isSuperAdmin: userRoles?.some(r => r.role === 'super_admin'),
    isAdmin: userRoles?.some(r => r.role === 'admin'),
    isEmployee: userRoles?.some(r => r.role === 'employee'),
  };
}
